/**
 * "Уникальность" — audio half. Mirrors server/src/exporting/uniqueness.ts's audio techniques
 * (pitch shift, EQ, volume, resample, background noise) as pure sample-array math, applied ONCE
 * to the whole timeline's already-mixed-down AudioBuffer (see media/audio.ts's
 * createTimelineAudioBuffer) right before it's handed to the encoder — not per-clip, since
 * there's no per-clip ffmpeg encode step in this pipeline to hook into the way cmss does.
 *
 * `speedChange` (cmss's shared video+audio tempo toggle) has no counterpart here — see
 * services/renderer/uniqueness.ts's top comment for why. `pitchShift` doesn't have that problem
 * (duration-preserving by construction — see pitchShiftSamples below) and IS supported.
 *
 * All the actual DSP is plain Float32Array-in/Float32Array-out math with zero browser-API
 * dependencies — only applyAudioUniqueness (the AudioBuffer-facing wrapper at the bottom) touches
 * real Web Audio types, which keeps the algorithms themselves unit-testable outside a browser.
 */

export interface AudioUniquenessOptions {
	pitchShift?: boolean;
	eq?: boolean;
	volumeDynamics?: boolean;
	resample?: boolean;
	backgroundNoise?: boolean;
}

const OPTION_KEYS: Array<keyof AudioUniquenessOptions> = [
	"pitchShift",
	"eq",
	"volumeDynamics",
	"resample",
	"backgroundNoise",
];

/** Checks only the 5 audio-side keys — see services/renderer/uniqueness.ts's matching
 * isVideoUniquenessEnabled for the video-side counterpart and why each module ignores the
 * other's keys in a combined options object. */
export function isAudioUniquenessEnabled(options?: AudioUniquenessOptions | null): boolean {
	if (!options) return false;
	return OPTION_KEYS.some((key) => options[key] === true);
}

export interface AudioUniquenessProfile {
	pitchRatio: number;
	eqFrequency: number;
	eqGainDb: number;
	volumeDb: number;
	/** Intermediate sample rate the resample technique bounces through before returning to the
	 * buffer's native rate — see resampleArtifact. */
	resampleRateHz: number;
	noiseAmplitude: number;
	noiseWeight: number;
}

function randRange({ rng, min, max }: { rng: () => number; min: number; max: number }): number {
	return min + rng() * (max - min);
}

function randSign(rng: () => number): 1 | -1 {
	return rng() < 0.5 ? -1 : 1;
}

export function buildAudioUniquenessProfile(rng: () => number = Math.random): AudioUniquenessProfile {
	return {
		pitchRatio: 1 + randSign(rng) * randRange({ rng, min: 0.005, max: 0.03 }),
		eqFrequency: Math.round(randRange({ rng, min: 200, max: 4000 })),
		eqGainDb: randSign(rng) * randRange({ rng, min: 1, max: 3 }),
		volumeDb: randSign(rng) * randRange({ rng, min: 0.5, max: 2 }),
		resampleRateHz: Math.round(randRange({ rng, min: 26000, max: 37000 })),
		noiseAmplitude: randRange({ rng, min: 0.0008, max: 0.003 }),
		noiseWeight: randRange({ rng, min: 0.02, max: 0.05 }),
	};
}

// ---------------------------------------------------------------------------------------------
// Pure sample-array algorithms
// ---------------------------------------------------------------------------------------------

/** Linear-interpolation resample to an arbitrary output length — used both by the pitch shifter
 * (to reinterpret the sample data at a different rate, same idea as ffmpeg's asetrate) and by the
 * resample-artifact technique (to actually change apparent sample rate and back). */
export function resampleLinear({
	input,
	outputLength,
}: {
	input: Float32Array;
	outputLength: number;
}): Float32Array {
	if (outputLength <= 0 || input.length === 0) return new Float32Array(0);
	if (outputLength === input.length) return input.slice();
	const output = new Float32Array(outputLength);
	const ratio = (input.length - 1) / Math.max(1, outputLength - 1);
	for (let i = 0; i < outputLength; i++) {
		const pos = i * ratio;
		const i0 = Math.floor(pos);
		const i1 = Math.min(i0 + 1, input.length - 1);
		const frac = pos - i0;
		output[i] = input[i0] * (1 - frac) + input[i1] * frac;
	}
	return output;
}

function hannWindow({ size }: { size: number }): Float32Array {
	const window = new Float32Array(size);
	for (let i = 0; i < size; i++) {
		window[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / Math.max(1, size - 1));
	}
	return window;
}

/**
 * WSOLA (Waveform-Similarity Overlap-Add) time stretch — output length ≈ input.length * factor,
 * pitch unaffected. Synthesis hop (spacing in the OUTPUT) is fixed at 50% of the window; the
 * analysis hop (spacing read from the INPUT) is scaled by 1/factor, so for factor > 1 (stretch)
 * consecutive windows re-read overlapping/repeated input material — that repetition is what
 * actually fills the extra output duration while preserving pitch. Naively reading each window at
 * exactly its ideal position produces audible phase-cancellation artifacts (verified directly —
 * for a pure test tone it silently cancelled the pitch shift back to the original; see this
 * function's own unit test script): each new window's start is instead nudged within
 * ±searchRadius samples of its ideal position to the offset that best cross-correlates with the
 * tail of the previously-placed window, so overlapping copies reinforce instead of cancelling.
 *
 * O(searchRadius × overlapLength) per window — only runs when pitchShift is actually enabled, and
 * only once per export (on the final mixed-down buffer, not per frame), so the cost is bounded and
 * paid once, not repeatedly.
 */
export function timeStretch({
	input,
	factor,
	sampleRate,
}: {
	input: Float32Array;
	factor: number;
	sampleRate: number;
}): Float32Array {
	if (input.length === 0 || factor <= 0) return new Float32Array(0);
	const windowSize = Math.max(64, Math.round(sampleRate * 0.05));
	const hopOut = Math.round(windowSize / 2);
	const idealHopIn = hopOut / factor;
	const overlapLength = windowSize - hopOut;
	const searchRadius = Math.max(1, Math.round(windowSize / 8));
	const window = hannWindow({ size: windowSize });
	const targetLength = Math.max(1, Math.round(input.length * factor));

	const output = new Float32Array(targetLength + windowSize);
	const windowSum = new Float32Array(targetLength + windowSize);

	let idealInPos = 0;
	let outPos = 0;
	let prevWindow: Float32Array | null = null;

	while (outPos < targetLength) {
		let inPos = Math.round(idealInPos);

		if (prevWindow) {
			// Best-matching offset within ±searchRadius of the ideal position: the one whose first
			// `overlapLength` samples best correlate with prevWindow's LAST `overlapLength` samples
			// (the region the two windows will actually overlap-add over).
			let bestScore = -Infinity;
			let bestDelta = 0;
			for (let delta = -searchRadius; delta <= searchRadius; delta++) {
				const candidate = inPos + delta;
				if (candidate < 0 || candidate + overlapLength > input.length) continue;
				let score = 0;
				for (let i = 0; i < overlapLength; i += 2) {
					// Every other sample — halves the search cost; single-sample precision isn't
					// needed here, just enough to land near the true best-correlated offset.
					score += input[candidate + i] * prevWindow[hopOut + i];
				}
				if (score > bestScore) {
					bestScore = score;
					bestDelta = delta;
				}
			}
			inPos = Math.max(0, Math.min(input.length - 1, inPos + bestDelta));
		}

		const chunkLength = Math.min(windowSize, input.length - inPos);
		if (chunkLength <= 0) break;
		const placedWindow = new Float32Array(windowSize);
		for (let i = 0; i < chunkLength; i++) {
			const w = window[i];
			const sample = input[inPos + i];
			placedWindow[i] = sample;
			output[outPos + i] += sample * w;
			windowSum[outPos + i] += w;
		}
		prevWindow = placedWindow;

		idealInPos += idealHopIn;
		outPos += hopOut;
	}

	for (let i = 0; i < output.length; i++) {
		if (windowSum[i] > 1e-6) output[i] /= windowSum[i];
	}
	return output.slice(0, targetLength);
}

/**
 * Duration-preserving pitch shift: resample down/up by `ratio` (reinterprets the same samples at
 * a different rate — changes both pitch and duration, like ffmpeg's asetrate), then time-stretch
 * by `ratio` to restore the original duration without further touching pitch (like ffmpeg's
 * compensating atempo). Net effect: pitch scaled by `ratio`, duration unchanged.
 */
export function pitchShiftSamples({
	input,
	sampleRate,
	ratio,
}: {
	input: Float32Array;
	sampleRate: number;
	ratio: number;
}): Float32Array {
	if (input.length === 0 || ratio <= 0) return input.slice();
	const resampled = resampleLinear({ input, outputLength: Math.max(1, Math.round(input.length / ratio)) });
	const restretched = timeStretch({ input: resampled, factor: ratio, sampleRate });
	// timeStretch's window-hop rounding can leave the result a handful of samples short/long of
	// the original — pad or trim so every channel comes back out at exactly the buffer's original
	// length (callers rely on all channels staying the same length as each other and as before).
	if (restretched.length === input.length) return restretched;
	const fixed = new Float32Array(input.length);
	fixed.set(restretched.subarray(0, Math.min(restretched.length, input.length)));
	return fixed;
}

interface BiquadCoefficients {
	b0: number;
	b1: number;
	b2: number;
	a1: number;
	a2: number;
}

/** RBJ Audio EQ Cookbook peaking-EQ coefficients (normalized, a0 already divided out). */
function peakingEqCoefficients({
	sampleRate,
	freq,
	gainDb,
	q,
}: {
	sampleRate: number;
	freq: number;
	gainDb: number;
	q: number;
}): BiquadCoefficients {
	const A = 10 ** (gainDb / 40);
	const w0 = (2 * Math.PI * freq) / sampleRate;
	const alpha = Math.sin(w0) / (2 * q);
	const cosw0 = Math.cos(w0);

	const a0 = 1 + alpha / A;
	return {
		b0: (1 + alpha * A) / a0,
		b1: (-2 * cosw0) / a0,
		b2: (1 - alpha * A) / a0,
		a1: (-2 * cosw0) / a0,
		a2: (1 - alpha / A) / a0,
	};
}

function applyBiquad({ input, coeffs }: { input: Float32Array; coeffs: BiquadCoefficients }): Float32Array {
	const output = new Float32Array(input.length);
	let x1 = 0;
	let x2 = 0;
	let y1 = 0;
	let y2 = 0;
	for (let i = 0; i < input.length; i++) {
		const x0 = input[i];
		const y0 = coeffs.b0 * x0 + coeffs.b1 * x1 + coeffs.b2 * x2 - coeffs.a1 * y1 - coeffs.a2 * y2;
		output[i] = y0;
		x2 = x1;
		x1 = x0;
		y2 = y1;
		y1 = y0;
	}
	return output;
}

/** Small peaking bump/cut at `freq` — Q=1 (roughly one octave wide), gain in dB. */
export function applyPeakingEq({
	input,
	sampleRate,
	freq,
	gainDb,
}: {
	input: Float32Array;
	sampleRate: number;
	freq: number;
	gainDb: number;
}): Float32Array {
	return applyBiquad({ input, coeffs: peakingEqCoefficients({ sampleRate, freq, gainDb, q: 1 }) });
}

export function applyVolumeDb({ input, gainDb }: { input: Float32Array; gainDb: number }): Float32Array {
	const gain = 10 ** (gainDb / 20);
	const output = new Float32Array(input.length);
	for (let i = 0; i < input.length; i++) {
		output[i] = Math.max(-1, Math.min(1, input[i] * gain));
	}
	return output;
}

/** Bounces through a lower intermediate sample rate and back — a cheap, deliberate way to shave
 * off a bit of high-frequency content as a resampling artifact, same intent as ffmpeg's
 * `aresample=X,aresample=origRate` pair. */
export function resampleArtifact({
	input,
	originalRate,
	intermediateRate,
}: {
	input: Float32Array;
	originalRate: number;
	intermediateRate: number;
}): Float32Array {
	const downLength = Math.max(1, Math.round((input.length * intermediateRate) / originalRate));
	const down = resampleLinear({ input, outputLength: downLength });
	return resampleLinear({ input: down, outputLength: input.length });
}

/** Adds a very quiet noise bed. `color: "white"` is flat-spectrum noise; `"pink"` (the default,
 * matching cmss's anoisesrc=c=pink) approximates 1/f noise via a cheap one-pole filter — softer
 * and less hissy than white noise at the same amplitude. */
export function mixInNoise({
	input,
	amplitude,
	weight,
	color = "pink",
}: {
	input: Float32Array;
	amplitude: number;
	weight: number;
	color?: "white" | "pink";
}): Float32Array {
	const output = new Float32Array(input.length);
	let pinkState = 0;
	for (let i = 0; i < input.length; i++) {
		const white = (Math.random() * 2 - 1) * amplitude;
		let noise = white;
		if (color === "pink") {
			pinkState = 0.98 * pinkState + 0.02 * white;
			noise = pinkState * 3; // compensate the one-pole filter's gain loss so amplitude stays meaningful
		}
		output[i] = Math.max(-1, Math.min(1, input[i] + noise * weight * 20));
	}
	return output;
}

// ---------------------------------------------------------------------------------------------
// AudioBuffer-facing wrapper — the only part of this file that touches real Web Audio types
// ---------------------------------------------------------------------------------------------

/** Every pure function above constructs its result via plain `new Float32Array(n)`, which
 * TypeScript's newer typed-array generics infer as `Float32Array<ArrayBufferLike>` rather than
 * the narrower `Float32Array<ArrayBuffer>` that AudioBuffer.getChannelData()/copyToChannel()
 * expect — both are ArrayBuffer-backed at runtime (nothing here ever touches a
 * SharedArrayBuffer), so this is a real, deliberate copy (not just a type-only cast) that also
 * happens to produce a value TypeScript accepts without an unsafe assertion. */
function toArrayBufferBacked(data: Float32Array): Float32Array<ArrayBuffer> {
	return new Float32Array(data);
}

/** Applies every enabled technique, in a fixed order (pitch → EQ → volume → resample →
 * background noise), to a fresh copy of `buffer` — the input buffer is left untouched. Safe to
 * call unconditionally; returns `buffer` itself unchanged when nothing is enabled. */
export function applyAudioUniqueness({
	buffer,
	options,
	profile,
}: {
	buffer: AudioBuffer;
	options: AudioUniquenessOptions;
	profile: AudioUniquenessProfile;
}): AudioBuffer {
	if (!isAudioUniquenessEnabled(options)) return buffer;

	const output = new AudioBuffer({
		numberOfChannels: buffer.numberOfChannels,
		length: buffer.length,
		sampleRate: buffer.sampleRate,
	});

	for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
		let data: Float32Array = buffer.getChannelData(channel).slice();

		if (options.pitchShift) {
			data = pitchShiftSamples({ input: data, sampleRate: buffer.sampleRate, ratio: profile.pitchRatio });
		}
		if (options.eq) {
			data = applyPeakingEq({
				input: data,
				sampleRate: buffer.sampleRate,
				freq: profile.eqFrequency,
				gainDb: profile.eqGainDb,
			});
		}
		if (options.volumeDynamics) {
			data = applyVolumeDb({ input: data, gainDb: profile.volumeDb });
		}
		if (options.resample) {
			data = resampleArtifact({
				input: data,
				originalRate: buffer.sampleRate,
				intermediateRate: profile.resampleRateHz,
			});
		}
		if (options.backgroundNoise) {
			data = mixInNoise({ input: data, amplitude: profile.noiseAmplitude, weight: profile.noiseWeight });
		}

		output.copyToChannel(toArrayBufferBacked(data.subarray(0, buffer.length)), channel);
	}

	return output;
}
